import type { ReactNode } from 'react';
import type { CampaignMember, TimelineEvent, TimelineLink, TimelineVisibility } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import type { TranslationKeys } from '../../../i18n';
import type { OpenChronicleLink } from '../contracts';

type T = (key: keyof TranslationKeys) => string;

/** A visibility choice of the row switch. */
export type VisibilityMode = TimelineVisibility['mode'];

/**
 * What a letopis row may do. Every GM-only action is absent for a player, so
 * a player's row never offers editing (their own suggestions are edited in
 * «My suggestions» only).
 */
export interface EventRowActions {
  readonly isGM: boolean;
  readonly onEdit?: (event: TimelineEvent) => void;
  readonly onDelete?: (eventId: string) => void;
  /** «+ link» on a published event without links. */
  readonly onAddLink?: (event: TimelineEvent) => void;
  /** The row visibility switch; showing to players is confirmed by the panel. */
  readonly onVisibility?: (event: TimelineEvent, mode: VisibilityMode) => void;
  /** Absent: links are shown, not clickable. */
  readonly onOpenLink?: OpenChronicleLink;
  /** Campaign members: the source of a published row («GM» / «auto» / a player's name). */
  readonly members?: readonly CampaignMember[];
  /** A map's current name (undefined: the map is gone). */
  readonly pageName: (pageId: string) => string | undefined;
  /** L-9: the form of the event being edited takes this event's row (inside its `li`, in the second column). */
  readonly editing?: EditingRow;
}

/** The row an open form stands in: its event and the form. */
export interface EditingRow {
  readonly eventId: string;
  readonly form: ReactNode;
}

/**
 * The row of an event whose form is open: the dot stays, the form takes the second column. The title stays
 * in the row for screen readers (the row keeps its name) — the form shows it in its own field.
 */
export const EDITING_TITLE = 'sr-only';

/** An auto draft: written by the server (combat, rest, transition, time…). */
export const isAutoEvent = (event: Pick<TimelineEvent, 'source'>): boolean => event.source === 'system';

/** A draft a player suggested (not auto, not written by a GM). */
export function isPlayerSuggestion(event: TimelineEvent, members: readonly CampaignMember[]): boolean {
  if (isAutoEvent(event)) return false;
  return members.find(m => m.id === event.createdBy)?.role !== 'gm';
}

/** «auto» / «GM» / the player's display name — never a member id. */
export function sourceLabel(event: TimelineEvent, members: readonly CampaignMember[], t: T): string {
  if (isAutoEvent(event)) return t('timeline.drafts.sourceAuto');
  const author = members.find(m => m.id === event.createdBy);
  if (author === undefined) return t('timeline.drafts.sourcePlayer');
  return author.role === 'gm' ? t('timeline.drafts.sourceGm') : author.name;
}

/**
 * The links a row shows: the event's own, plus its maps (`pageIds`, e.g. a
 * transition) that no page link already names. A map link carries the map's
 * current name; a map that is gone is not offered.
 */
export function rowLinks(event: TimelineEvent, pageName: (pageId: string) => string | undefined): TimelineLink[] {
  const own = event.links.map(link => {
    if (link.targetType !== 'page') return link;
    const name = pageName(link.targetId);
    return name !== undefined ? { ...link, displayName: name } : link;
  });
  const linkedPages = new Set(own.filter(l => l.targetType === 'page').map(l => l.targetId));
  const maps: TimelineLink[] = [];
  for (const pageId of event.pageIds ?? []) {
    if (linkedPages.has(pageId)) continue;
    const name = pageName(pageId);
    if (name === undefined) continue;
    linkedPages.add(pageId);
    maps.push({ id: `page:${pageId}`, targetType: 'page', targetId: pageId, displayName: name });
  }
  return [...own, ...maps];
}

/** True when `to` lets somebody see the event who does not see it under `from`. */
export function widensVisibility(from: TimelineVisibility, to: TimelineVisibility): boolean {
  if (to.mode === 'gm' || from.mode === 'all') return false;
  if (to.mode === 'all') return true;
  const before = new Set(from.mode === 'players' ? from.playerIds ?? [] : []);
  return (to.playerIds ?? []).some(id => !before.has(id));
}

type CreatedFields = Pick<TimelineEvent, 'title' | 'description' | 'type' | 'time' | 'visibility' | 'tags' | 'links'>
  & Partial<Pick<TimelineEvent, 'sessionId' | 'pageIds'>>;

/**
 * The content of a created event as the server keeps it from the create
 * payload (title trimmed, defaults filled, link ids aside): a payload and the
 * event it made give the same string.
 */
export function createdContent(event: CreatedFields): string {
  return JSON.stringify([
    event.title.trim(),
    event.description || '',
    event.type,
    event.time || { label: '', sortKey: 0 },
    event.visibility,
    (event.tags || []).slice(0, LIMITS.MAX_TIMELINE_TAGS),
    event.sessionId ?? null,
    (event.links ?? []).map(l => [l.targetType, l.targetId]),
    event.pageIds ?? null,
  ]);
}

/**
 * The draft a confirmed «Add event» made, by its whole content: never another
 * event that only shares the title. Two new drafts with the same content (two
 * tabs of the GM) are ambiguous — 'ambiguous' publishes neither, both wait in
 * «Waiting for publication». undefined: not arrived yet.
 */
export function arrivedDraft(
  drafts: readonly TimelineEvent[],
  wanted: { content: string; known: ReadonlySet<string>; author: string },
): TimelineEvent | 'ambiguous' | undefined {
  const found = drafts.filter(d =>
    !wanted.known.has(d.id) && d.createdBy === wanted.author && createdContent(d) === wanted.content);
  if (found.length > 1) return 'ambiguous';
  return found[0];
}

/** Fill `{name}` placeholders of a translated string. */
export function fill(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (all, key: string) => (key in values ? String(values[key]) : all));
}

/**
 * What a row calls an event. The server writes the start of a session as «Session #3: Title» (it does not know
 * the language of the screen): that one line is said in the UI's language here; every other title is the author's own.
 */
export function eventTitle(event: Pick<TimelineEvent, 'title' | 'type'>, t: T): string {
  if (event.type !== 'session-start') return event.title;
  const m = /^Session #(\d+)(?:: (.*))?$/.exec(event.title);
  if (m === null) return event.title;
  return m[2] === undefined || m[2] === ''
    ? fill(t('timeline.session.startEvent'), { number: m[1] })
    : fill(t('timeline.session.startEventTitled'), { number: m[1], title: m[2] });
}
